/**
 * LabelBlock Style Fields - Style configurations for template label parameters
 *
 * Used for styling labels in Text.parameters.labels dictionary.
 * Based on LabelBlock schema from @segmently/schema-models.
 *
 * Sprint: screen-editor-body
 * Task: Phase 1 - labelBlockFields factory
 */

import type { FieldConfig } from '../../types/editorFieldTypes';
import {
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  TEXT_ALIGN_OPTIONS,
  NUMBER_0_50_OPTIONS,
} from './options';

/**
 * LabelBlock style fields for template parameters.labels entries
 *
 * Order matches AddLabelDialog layout (2 columns, left to right, top to bottom):
 * Row 1: font family | line height
 * Row 2: font size | font weight
 * Row 3: text color | background color
 * Row 4: text align | line through color
 * Row 5: padding (handled separately via PaddingElement)
 *
 * isNullable: true - All fields are optional for labels
 */
export const LABEL_BLOCK_STYLE_FIELDS: FieldConfig[] = [
  // Row 1: Font Family | Line Height
  {
    path: '.fontFamily',
    label: 'font family',
    type: 'select',
    options: [...FONT_FAMILY_OPTIONS],
    defaultValue: 'SF Pro',
    isNullable: true,
  },
  {
    path: '.lineHeight',
    label: 'line height',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 32,
    defaultScrollValue: 18,
    isNullable: true,
  },
  // Row 2: Font Size | Font Weight
  {
    path: '.fontSize',
    label: 'font size',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 18,
    defaultScrollValue: 18,
    isNullable: true,
  },
  {
    path: '.fontWeight',
    label: 'font weight',
    type: 'select',
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    defaultValue: 100,
    isNullable: true,
    numericValue: true,
  },
  // Row 3: Text Color | Background Color
  {
    path: '.color',
    label: 'text color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
  {
    path: '.backgroundColor',
    label: 'background color',
    type: 'color',
    defaultValue: '#ffffff',
    isNullable: true,
  },
  // Row 4: Text Align | Line Through Color
  {
    path: '.textAlign',
    label: 'text align',
    type: 'select',
    options: [...TEXT_ALIGN_OPTIONS],
    defaultValue: 'center',
    isNullable: true,
  },
  {
    path: '.textDecorationColor',
    label: 'line through color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
];

/**
 * LabelBlock padding fields (for Box.styles padding)
 *
 * Row 5: Padding fields (paddingLeft, paddingRight, paddingTop, paddingBottom)
 */
export const LABEL_BLOCK_PADDING_FIELDS: FieldConfig[] = [
  {
    path: '.paddingLeft',
    label: 'box padding left',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 16,
    defaultScrollValue: 16,
    isNullable: true,
  },
  {
    path: '.paddingRight',
    label: 'box padding right',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 16,
    defaultScrollValue: 16,
    isNullable: true,
  },
  {
    path: '.paddingTop',
    label: 'box padding top',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 32,
    defaultScrollValue: 16,
    isNullable: true,
  },
  {
    path: '.paddingBottom',
    label: 'box padding bottom',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 0,
    defaultScrollValue: 0,
    isNullable: true,
  },
];

/**
 * All LabelBlock fields (styles + padding)
 */
export const ALL_LABEL_BLOCK_FIELDS: FieldConfig[] = [
  ...LABEL_BLOCK_STYLE_FIELDS,
  ...LABEL_BLOCK_PADDING_FIELDS,
];

/**
 * Create LabelBlock style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'title.parameters.labels.highlight')
 * @param template - Style fields template to use (default: LABEL_BLOCK_STYLE_FIELDS)
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createLabelBlockStyleFields('title.parameters.labels.highlight')
 * // Returns fields with paths like 'title.parameters.labels.highlight.fontSize'
 */
export function createLabelBlockStyleFields(
  basePath: string,
  template: FieldConfig[] = LABEL_BLOCK_STYLE_FIELDS
): FieldConfig[] {
  return template.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Create all LabelBlock fields (styles + padding) with full path prefix
 *
 * @param basePath - Base path to prepend
 * @returns Array of FieldConfig with full paths
 */
export function createAllLabelBlockFields(basePath: string): FieldConfig[] {
  return ALL_LABEL_BLOCK_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}
